import { useEffect } from 'react';
import { isWebGL2Available } from './gl/context';
import { hasSeenTutorial, messagesFor } from './i18n';
import { openFilesFromPicker } from './state/openFiles';
import { autosaveNow, restoreSession } from './state/session';
import { useAppStore } from './state/store';
import { CanvasView } from './ui/CanvasView';
import { Dialogs } from './ui/dialogs';
import { MenuBar } from './ui/MenuBar';
import { StatusBar } from './ui/StatusBar';
import { TabBar } from './ui/TabBar';
import { Toolbar } from './ui/Toolbar';
import { ToolOptionsBar } from './ui/ToolOptionsBar';
import { ChannelsPanel } from './ui/panels/ChannelsPanel';
import { ColorPanel } from './ui/panels/ColorPanel';
import { HistoryPanel } from './ui/panels/HistoryPanel';
import { LayersPanel } from './ui/panels/LayersPanel';
import { NavigatorPanel } from './ui/panels/NavigatorPanel';
import { PanelFlyout } from './ui/panels/PanelFlyout';
import { PanelRail } from './ui/panels/PanelRail';
import { PanelTabGroup } from './ui/panels/PanelTabGroup';
import { PathsPanel } from './ui/panels/PathsPanel';
import './ui/styles.css';

function Welcome() {
  const openDialog = useAppStore((state) => state.openDialog);
  const locale = useAppStore((state) => state.locale);
  const t = messagesFor(locale);

  return (
    <div className="welcome">
      <div className="welcome__title">{t.welcomeTitle}</div>
      <p className="welcome__text">{t.welcomeText}</p>
      <div className="welcome__actions">
        <button
          type="button"
          className="button button--primary"
          onClick={() => openDialog({ kind: 'newDocument' })}
        >
          {t.welcomeNew}
        </button>
        <button
          type="button"
          className="button"
          onClick={() => void openFilesFromPicker()}
        >
          {t.welcomeOpen}
        </button>
        <button
          type="button"
          className="button"
          onClick={() => openDialog({ kind: 'tutorial' })}
        >
          {t.welcomeTutorial}
        </button>
      </div>
      <p className="welcome__text">{t.welcomeDrop}</p>
      <button
        type="button"
        className="welcome__lang"
        onClick={() => openDialog({ kind: 'language' })}
      >
        {t.language}
      </button>
    </div>
  );
}

function FatalError({ message }: { message: string }) {
  return (
    <div className="fatal">
      <div className="fatal__title">The editor cannot start</div>
      <p className="welcome__text">{message}</p>
      <button type="button" className="button button--primary" onClick={() => location.reload()}>
        Reload
      </button>
    </div>
  );
}

export function App() {
  const fatalError = useAppStore((state) => state.fatalError);
  const documents = useAppStore((state) => state.documents);
  const contextLost = useAppStore((state) => state.contextLost);
  const chromeHidden = useAppStore((state) => state.chromeHidden);
  const panelColumnHidden = useAppStore((state) => state.panelColumnHidden);
  const setFatalError = useAppStore((state) => state.setFatalError);

  useEffect(() => {
    if (!isWebGL2Available()) {
      setFatalError(
        'This browser does not support WebGL2. Update your browser or enable hardware acceleration in its settings.',
      );
    }
  }, [setFatalError]);

  useEffect(() => {
    const store = useAppStore.getState();
    if (store.fatalError) return;
    void restoreSession().then((count) => {
      if (count > 0) {
        store.setStatus(
          count === 1 ? 'Restored one document from autosave' : `Restored ${count} documents`,
        );
      }
    });
    if (!hasSeenTutorial()) {
      window.setTimeout(() => {
        if (!useAppStore.getState().dialog) {
          useAppStore.getState().openDialog({ kind: 'tutorial' });
        }
      }, 400);
    }
  }, []);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (useAppStore.getState().documents.some((doc) => doc.dirty)) event.preventDefault();
    };
    // Hiding the tab is the last reliable moment to flush pixels to IndexedDB.
    const onHidden = () => {
      if (document.visibilityState === 'hidden') void autosaveNow();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onHidden);
    };
  }, []);

  if (fatalError) return <FatalError message={fatalError} />;

  return (
    <div className="app">
      <MenuBar />
      {!chromeHidden && <ToolOptionsBar />}
      <div className="app__body">
        {!chromeHidden && <Toolbar />}
        <div className="app__center">
          <TabBar />
          <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
            <CanvasView />
            {documents.length === 0 && <Welcome />}
          </div>
        </div>
        {!chromeHidden && (
          <div className="app__right">
            <PanelRail />
            {!panelColumnHidden && (
              <div className="app__panels">
                <div className="panel-stack panel-stack--navigator">
                  <NavigatorPanel />
                </div>
                <PanelFlyout />
                <PanelTabGroup
                  stack="upper"
                  className="panel-stack--upper"
                  tabs={[
                    { id: 'color', label: 'Swatches', content: <ColorPanel /> },
                    { id: 'history', label: 'History', content: <HistoryPanel /> },
                  ]}
                />
                <PanelTabGroup
                  stack="lower"
                  className="panel-stack--lower"
                  tabs={[
                    { id: 'layers', label: 'Layers', content: <LayersPanel /> },
                    { id: 'channels', label: 'Channels', content: <ChannelsPanel /> },
                    { id: 'paths', label: 'Paths', content: <PathsPanel /> },
                  ]}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <StatusBar />
      <Dialogs />
      {contextLost && (
        <div className="context-lost">
          <div className="context-lost__box">
            The graphics context was reset. Restoring documents from autosave…
          </div>
        </div>
      )}
    </div>
  );
}
